import { toRaw } from "vue";
import MiniSearch from "minisearch";
import { createStorageService, type StorageEntity } from "./storage";
import type { FoodItem } from "@macro-tracker/models";
import type { Recipe } from "./recipes";

export type LocalFoodEntityType =
  | "system-food"
  | "custom-food"
  | "shared-food"
  | "custom-recipe"
  | "shared-recipe";

type LocalFoodSearchDoc = StorageEntity & {
  entityId: string;
  entityType: LocalFoodEntityType;
  name: string;
  brand?: string;
  tokens: string;
  updatedAt: string;
  isArchived?: boolean;
  isFavorite?: boolean;
  foodItem?: FoodItem;
};

type LocalFoodSearchResult = {
  id: string;
  score: number;
  entityId: string;
  entityType: LocalFoodEntityType;
  updatedAt: string;
  foodItem?: FoodItem;
};

const DOC_NAMESPACE = "macrotracker:foods-search-docs";

const docStorage = createStorageService<LocalFoodSearchDoc>(DOC_NAMESPACE);

let indexReady = false;
let index: MiniSearch<LocalFoodSearchDoc> | null = null;
const docsById = new Map<string, LocalFoodSearchDoc>();

const buildIndex = (): MiniSearch<LocalFoodSearchDoc> =>
  new MiniSearch<LocalFoodSearchDoc>({
    idField: "id",
    fields: ["name", "brand", "tokens"],
    storeFields: ["id", "entityId", "entityType", "updatedAt", "foodItem"],
    searchOptions: {
      prefix: true,
      fuzzy: 0.2,
      boost: {
        name: 3,
        brand: 1.5,
      },
    },
  });

const normalize = (value: string | undefined | null): string =>
  (value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const uniqTokens = (values: Array<string | undefined | null>): string => {
  const merged = values
    .map((v) => normalize(v))
    .filter(Boolean)
    .join(" ");
  const set = new Set(merged.split(/[^a-z0-9]+/g).filter(Boolean));
  return Array.from(set).join(" ");
};

const docId = (entityType: LocalFoodEntityType, entityId: string): string =>
  `${entityType}:${entityId}`;

const ensureIndexLoaded = async (): Promise<void> => {
  if (indexReady && index) {
    return;
  }

  const docs = await docStorage.getAll();
  docsById.clear();
  index = buildIndex();

  for (const doc of docs) {
    if (doc.isArchived) {
      continue;
    }
    docsById.set(doc.id, doc);
  }

  if (docsById.size > 0) {
    index.addAll(Array.from(docsById.values()));
  }

  indexReady = true;
};

const removeFromIndexIfPresent = (
  searchIndex: MiniSearch<LocalFoodSearchDoc>,
  doc: LocalFoodSearchDoc,
): void => {
  try {
    searchIndex.remove(doc);
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes("cannot remove document") &&
      error.message.includes("it is not in the index")
    ) {
      return;
    }
    throw error;
  }
};

const upsertDoc = async (doc: LocalFoodSearchDoc): Promise<void> => {
  await ensureIndexLoaded();
  if (!index) {
    return;
  }

  const previous = docsById.get(doc.id);
  if (previous) {
    removeFromIndexIfPresent(index, previous);
  }

  await docStorage.save(doc);

  if (doc.isArchived) {
    docsById.delete(doc.id);
    return;
  }

  docsById.set(doc.id, doc);
  index.add(doc);
};

export const removeLocalFoodSearchDoc = async (
  entityType: LocalFoodEntityType,
  entityId: string,
): Promise<void> => {
  await ensureIndexLoaded();
  if (!index) {
    return;
  }

  const id = docId(entityType, entityId);
  const existing = docsById.get(id);
  if (existing) {
    removeFromIndexIfPresent(index, existing);
    docsById.delete(id);
  }
  await docStorage.remove(id);
};

export const upsertFoodItemInLocalSearch = async (
  food: FoodItem,
  entityType: Extract<LocalFoodEntityType, "system-food" | "custom-food" | "shared-food">,
): Promise<void> => {
  const id = docId(entityType, food.id);
  const servingLabels = food.servingSizes.map((s) => s.label).join(" ");

  await upsertDoc({
    id,
    entityId: food.id,
    entityType,
    name: food.name,
    brand: food.brand,
    tokens: uniqTokens([
      food.name,
      food.brand,
      servingLabels,
      food.upc,
      entityType,
    ]),
    updatedAt: new Date().toISOString(),
    isArchived: food.isArchived,
    isFavorite: food.isFavorite,
    foodItem: toRaw(food),
  });
};

export const upsertRecipeInLocalSearch = async (
  recipe: Recipe,
  entityType: Extract<LocalFoodEntityType, "custom-recipe" | "shared-recipe">,
): Promise<void> => {
  const ingredients = recipe.ingredients
    .map((ingredient) => {
      if ("kind" in ingredient && ingredient.kind === "food") {
        return `${ingredient.foodName} ${ingredient.foodBrand ?? ""}`;
      }
      return "name" in ingredient ? ingredient.name : "";
    })
    .join(" ");

  await upsertDoc({
    id: docId(entityType, recipe.id),
    entityId: recipe.id,
    entityType,
    name: recipe.name,
    tokens: uniqTokens([recipe.name, ingredients, entityType]),
    updatedAt: new Date().toISOString(),
    isArchived: recipe.isArchived,
  });
};

export const searchLocalFoods = async (
  query: string,
  options: {
    limit?: number;
    includeRecipes?: boolean;
  } = {},
): Promise<LocalFoodSearchResult[]> => {
  await ensureIndexLoaded();
  if (!index) {
    return [];
  }

  const trimmed = query.trim();
  if (!trimmed) {
    return [];
  }

  const includeRecipes = options.includeRecipes ?? false;
  const results = index.search(trimmed, {
    prefix: true,
    fuzzy: 0.15,
    boost: {
      name: 3,
      brand: 1.5,
    },
  });

  const localResults: LocalFoodSearchResult[] = [];
  for (const row of results) {
    const doc = docsById.get(String(row.id));
    if (!doc) {
      continue;
    }
    localResults.push({
      id: doc.id,
      score: row.score,
      entityId: doc.entityId,
      entityType: doc.entityType,
      updatedAt: doc.updatedAt,
      foodItem: doc.foodItem,
    });
  }

  return localResults
    .filter((row) => (includeRecipes ? true : !row.entityType.includes("recipe")))
    .slice(0, options.limit ?? 50);
};

export const rebuildLocalFoodSearchIndex = async (payload: {
  foodItems: FoodItem[];
  recipes: Recipe[];
}): Promise<void> => {
  index = buildIndex();
  docsById.clear();
  indexReady = true;

  await docStorage.clear();

  for (const food of payload.foodItems) {
    await upsertFoodItemInLocalSearch(food, food.isCustom ? "custom-food" : "system-food");
  }

  for (const recipe of payload.recipes) {
    await upsertRecipeInLocalSearch(recipe, "custom-recipe");
  }
};
